<script setup>
import { reactive, computed, ref } from 'vue'

import ParticipateDropdown from '@/components/ParticipateDropdown.vue'

import { useAssignment } from '@/composables/useAssignment.js'
import { usePairingStore } from '@/stores/PairingStore.js'
import { useParticipateStore } from '@/stores/ParticipateStore.js'
import { useSettingsStore } from '@/stores/SettingsStore.js'
import { useTableStore } from '@/stores/TableStore.js'

const pairingStore = usePairingStore()
const participateStore = useParticipateStore()
const settingsStore = useSettingsStore()
const tableStore = useTableStore()

const { assignAllParticipates } = useAssignment()

let participate = reactive({ name: '' })
let bulkEntry = reactive({ input: '', processedNamesFormat: 'FirstNameLastName', processedNames: [], alertMessage: '' })

function addParticipate() {
    let name = participate.name.trim()
    if (name) {
        participateStore.add(name)
    }
    participate.name = ''
}

function addBulkParticipates() {
    let addCount = 0
    bulkEntry.processedNames.forEach((processedName) => {
        if (processedName.status === 'Will Be Added') {
            if (bulkEntry.processedNamesFormat == 'FirstNameLastName') {
                participateStore.add([...processedName.parts].join(' '))
            } else if (bulkEntry.processedNamesFormat == 'LastNameFirstName') {
                participateStore.add([...processedName.parts].reverse().join(' '))
            }

            addCount++
        }
    })

    if (addCount == 0) {
        bulkEntry.alertMessageType = 'danger'
        bulkEntry.alertMessage = `Whoops! Either those names exist already or you need to add names below...`
    } else if (addCount == 1) {
        bulkEntry.alertMessageType = 'success'
        bulkEntry.alertMessage = `You added 1 new participate!`
    } else if (addCount > 1) {
        bulkEntry.alertMessageType = 'success'
        bulkEntry.alertMessage = `You added ${addCount} new participates!`
    }

    if (addCount > 0) {
        bulkEntry.input = ''
        bulkEntry.processedNames = []
    }

    setTimeout(() => {
        bulkEntry.alertMessageType = 'danger'
        bulkEntry.alertMessage = ''
    }, 5000)
}

function processedNamesFormatInputChange() {
    bulkEntry.processedNames.forEach((processedNameToReview) => {
        processedNameToReview.status = isNameAlreadyAdd(processedNameToReview.parts)
    })
}

function bulkEntryInputChange() {
    bulkEntry.processedNames = []
    bulkEntry.input.split("\n").filter((line) => line != '').forEach((name) => {
        let nameParts = name.trim().split(/\t|,/).map((piece) => {
            return piece.trim()
        })
        bulkEntry.processedNames.push({ parts: nameParts, status: isNameAlreadyAdd(nameParts) })
    })
}

function isNameAlreadyAdd(nameParts) {
    let result = 'Will Be Added'
    participateStore.participates.forEach((participate) => {
        if (bulkEntry.processedNamesFormat === 'FirstNameLastName' && participate.name === [...nameParts].join(' ')) {
            result = 'Already Exists'
        }

        if (bulkEntry.processedNamesFormat === 'LastNameFirstName' && participate.name === [...nameParts].reverse().join(' ')) {
            result = 'Already Exists'
        }
    })
    return result
}


const participateSearch = ref('')

function clearParticipateSearch() {
    participateSearch.value = ''
}

const filteredParticipates = computed(() => {
    const q = participateSearch.value.toLowerCase().trim()
    if (!q) return participateStore.participates
    return participateStore.participates.filter(p => p.name.toLowerCase().includes(q))
})

const unassignedParticipates = computed(() => {
    const assigned = tableStore.assignedParticipateIds
    return participateStore.participates.filter(p => !assigned.has(p.uuid))
})

function startDragParticipate(event, participateID) {
    settingsStore.showAvailableSlots = true
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('participateID', participateID)
}

function endDragParticipate(event) {
    settingsStore.showAvailableSlots = false
    event.dataTransfer.setData('participateID', null)
}

const currentParticipateLimitProgress = computed(() => {
    return (participateStore.participates.length / settingsStore.participateLimit * 100.0).toFixed(0)
})

const bulkEntryModalTableData = computed(() => {
    let headers = []
    let data = []

    if (bulkEntry.processedNamesFormat === 'FirstNameLastName') {
        headers = ['First Name', 'Last Name', 'Status']
        bulkEntry.processedNames.forEach((name, i) => {
            data.push([name.parts[0], name.parts[1], name.status])
        })
    } else {
        headers = [ 'Last Name', 'First Name', 'Status']
        bulkEntry.processedNames.forEach((name, i) => {
            data.push([name.parts[0], name.parts[1], name.status])
        })
    }

    return {
        headers: headers,
        data: data
    }
})

</script>

<template>
    <div class="col card mb-4">
        <div class="card-header">
            <strong>Participates</strong>
        </div>
        <div class="card-body">
            <div class="vstack gap-3 mx-auto">
                <div class="input-group">
                    <input type="text" class="form-control" placeholder="Name" aria-label="Name" aria-describedby="Name" v-model="participate.name">
                    <button class="btn btn-primary" type="button" id="participateNameButton" @click="addParticipate">Add</button>
                    <button type="button" class="btn btn-primary dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
                        <span class="visually-hidden">Toggle Dropdown</span>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                        <li><button class="dropdown-item" type="button" id="bulkEntryButton" data-bs-toggle="modal" data-bs-target="#bulkEntryModal">Bulk Entry</button></li>
                    </ul>
                </div>

                <div>
                    Participate Limit: {{currentParticipateLimitProgress}}%
                    <progress id="currentParticipateLimitProgress" class="w-100" :value="currentParticipateLimitProgress" max="100"></progress>
                </div>

                <button class="btn btn-success" type="button" id="assignAllParticipates" @click="assignAllParticipates(true)">Randomly Assign All</button>
                <button class="btn btn-primary" type="button" data-bs-toggle="modal" data-bs-target="#viewAppParticipateModal">View All ({{ participateStore.participates.length }})</button>

                <div v-if="unassignedParticipates.length > 0">
                    <hr class="my-2">
                    <small class="text-muted d-block mb-1">Unassigned — drag to a table</small>
                    <div class="d-flex flex-wrap gap-1">
                        <span
                            v-for="p in unassignedParticipates"
                            :key="p.uuid"
                            class="badge text-bg-secondary"
                            style="cursor: grab; user-select: none;"
                            draggable="true"
                            @dragstart="startDragParticipate($event, p.uuid)"
                            @dragend="endDragParticipate($event)">
                            {{ p.name }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- View All Participates Modal -->
        <div class="modal fade modal-lg" id="viewAppParticipateModal" tabindex="-1" aria-labelledby="viewAppParticipateModalLabel" aria-hidden="true" @hidden.bs.modal="clearParticipateSearch">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5" id="viewAppParticipateModalLabel">Participates <span class="badge text-bg-secondary">{{ participateStore.participates.length }}</span></h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <input type="text" class="form-control mb-3" placeholder="Search participants…" v-model="participateSearch" autocomplete="off">
                        <p v-if="filteredParticipates.length === 0 && participateSearch" class="text-muted">No participants match your search.</p>
                        <div v-for="participate in filteredParticipates">
                            <ParticipateDropdown :participate="participate" :key="participate.uuid"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bulk Entry Modal -->
        <div class="modal fade modal-lg" id="bulkEntryModal" tabindex="-1" aria-labelledby="bulkEntryModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5" id="bulkEntryModalLabel">Participate Bulk Entry</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body">
                        <div v-if="bulkEntry.alertMessage !== ''" :class="'alert alert-' + bulkEntry.alertMessageType" role="alert">{{ bulkEntry.alertMessage }}</div>

                        <label for="bulkEntryTextArea" class="form-label">Participates<br/><small>Make sure that each participate is on a new line. You may use a comma to separate the parts of the name.</small></label>
                        <textarea class="form-control" id="bulkEntryTextArea" rows="10" v-model="bulkEntry.input" @input="bulkEntryInputChange"></textarea>

                        <hr>

                        <label for="bulkEntryProcessedTable" class="form-label">Processed Participates<br/><small>Here are the processed participate names</small></label>
                        <select class="form-select mb-1" aria-label="Naming Format Selection" @click="processedNamesFormatInputChange" v-model="bulkEntry.processedNamesFormat">
                            <option value="FirstNameLastName" selected>First Name, Last Name</option>
                            <option value="LastNameFirstName">Last Name, First Name</option>
                        </select>

                        <table id="bulkEntryProcessedTable" class="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th scope="col" v-for="header in bulkEntryModalTableData.headers">{{ header }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in bulkEntryModalTableData.data">
                                    <td v-for="part in item">{{ part }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        <button type="button" class="btn btn-primary" @click="addBulkParticipates">Add All ({{ bulkEntry.processedNames.length }})</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
</style>
